import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  InputLabel,
  Divider,
  Alert,
} from '@mui/material';
//import { useLanguage } from '../contexts/LanguageContext';
import { useContent } from '../contexts/ContentContext';

const TAB_OPTIONS = [
  { value: 'content', label: 'Content' },
  { value: 'images', label: 'Images' },
  { value: 'video', label: 'Video' },
  { value: 'laptops', label: 'Laptops' },
  { value: 'gamingPc', label: 'Gaming PC' },
];

const Admin = () => {
  const { content, updateContent } = useContent();
  const [localContent, setLocalContent] = useState(content);
  const [inputValues, setInputValues] = useState(content); // For immediate UI feedback
  const [images, setImages] = useState({ hero: '', gaming: [], brands: [], services: {} });
  const [video, setVideo] = useState({ type: 'youtube', youtubeUrl: '', uploadedVideo: '', thumbnail: '' });
  const [activeTab, setActiveTab] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [laptops, setLaptops] = useState([]);
  const [gamingPc, setGamingPc] = useState({
    baseCost: 299,
    baseCostLabelAr: '',
    baseCostLabelEn: '',
    orderButtonAr: '',
    orderButtonEn: '',
    disclaimerAr: '',
    disclaimerEn: '',
    categories: [],
    options: { cpu: [], gpu: [], ram: [], storage: [], cooling: [], psu: [] },
  });
  const [saveMessage, setSaveMessage] = useState(null);

  useEffect(() => {
    setLocalContent(content);
    setInputValues(content);
  }, [content]);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${process.env.REACT_APP_API_URL}/api/images`);
        if (r.ok) setImages(await r.json());
      } catch (e) {}
      try {
        const r = await fetch(`${process.env.REACT_APP_API_URL}/api/video`);
        if (r.ok) setVideo(await r.json());
      } catch (e) {}
    })();
  }, []);

  useEffect(() => {
    if (TAB_OPTIONS[activeTab]?.value === 'laptops') {
      fetch(`${process.env.REACT_APP_API_URL}/api/laptops`).then((r) => r.json()).then((d) => setLaptops(Array.isArray(d) ? d : [])).catch(() => setLaptops([]));
    }
    if (TAB_OPTIONS[activeTab]?.value === 'gamingPc') {
      fetch(`${process.env.REACT_APP_API_URL}/api/gaming-pc`)
        .then((r) => r.json())
        .then((data) => {
          // Ensure options object has all required keys
          const normalized = {
            ...data,
            options: {
              cpu: data.options?.cpu || [],
              gpu: data.options?.gpu || [],
              ram: data.options?.ram || [],
              storage: data.options?.storage || [],
              cooling: data.options?.cooling || [],
              psu: data.options?.psu || [],
            },
          };
          setGamingPc(normalized);
        })
        .catch(() => {
          // If fetch fails, ensure we have a valid structure
          setGamingPc({
            baseCost: 299,
            baseCostLabelAr: '',
            baseCostLabelEn: '',
            orderButtonAr: '',
            orderButtonEn: '',
            disclaimerAr: '',
            disclaimerEn: '',
            categories: [],
            options: { cpu: [], gpu: [], ram: [], storage: [], cooling: [], psu: [] },
          });
        });
    }
  }, [activeTab]);

  // Immediate handlers - update inputValues instantly for smooth native-like typing
  const handleContentChange = (lang, key, value) => {
    setInputValues((prev) => ({ ...prev, [lang]: { ...prev[lang], [key]: value } }));
  };

  const handleNestedContentChange = (lang, parentKey, childKey, value) => {
    setInputValues((prev) => ({
      ...prev,
      [lang]: { 
        ...prev[lang], 
        [parentKey]: { ...prev[lang][parentKey], [childKey]: value } 
      },
    }));
  };

  const handleNestedArrayItemChange = (lang, parentKey, arrayKey, index, field, value) => {
    setInputValues((prev) => {
      const arr = [...(prev[lang][parentKey]?.[arrayKey] || [])];
      arr[index] = { ...arr[index], [field]: value };
      return {
        ...prev,
        [lang]: {
          ...prev[lang],
          [parentKey]: {
            ...prev[lang][parentKey],
            [arrayKey]: arr
          }
        }
      };
    });
  };

  const handleArrayItemChange = (lang, arrayKey, index, field, value) => {
    setInputValues((prev) => {
      const arr = [...(prev[lang][arrayKey] || [])];
      arr[index] = { ...arr[index], [field]: value };
      return { ...prev, [lang]: { ...prev[lang], [arrayKey]: arr } };
    });
  };

  const saveContent = async () => {
    // Update localContent with inputValues before saving
    setLocalContent(inputValues);
    const ok = await updateContent(inputValues);
    setSaveMessage(ok ? 'Content saved.' : 'Failed to save.');
    setTimeout(() => setSaveMessage(null), 3000);
  };

  const handleImageUpload = async (e, section, index = null) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('image', file);
    try {
      // Pass section as query parameter for optimization
      const uploadUrl = `/api/images/upload?section=${encodeURIComponent(section)}`;
      const res = await fetch(uploadUrl, { method: 'POST', body: formData });
      if (!res.ok) throw new Error();
      const data = await res.json();
      
      // Log optimization results if available
      if (data.optimization) {
        console.log(`✅ Image optimized for "${section}": ${(data.optimization.savedPercent)}% size reduction`);
      }
      
      let next = { ...images };
      if (section === 'hero') next.hero = data.url;
      else if (section === 'gaming') next.gaming = index != null ? next.gaming.map((u, i) => (i === index ? data.url : u)) : [...next.gaming, data.url];
      else if (section === 'brands') next.brands = [...next.brands, data.url];
      else if (section === 'gallery') next.gallery = [...(next.gallery || []), data.url];
      else if (section.startsWith('service-')) next.services = { ...next.services, [section]: data.url };
      setImages(next);
      await fetch(`${process.env.REACT_APP_API_URL}/api/images`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(next) });
      setSaveMessage('Image uploaded and saved.');
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (err) {
      setSaveMessage('Upload failed.');
      setTimeout(() => setSaveMessage(null), 3000);
    } finally {
      setUploading(false);
    }
  };

  const saveVideo = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/video`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(video) });
      setSaveMessage(res.ok ? 'Video config saved.' : 'Failed.');
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (e) {
      setSaveMessage('Failed.');
      setTimeout(() => setSaveMessage(null), 3000);
    }
  };

  const saveLaptops = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/laptops`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(laptops) });
      setSaveMessage(res.ok ? 'Laptops saved.' : 'Failed.');
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (e) {
      setSaveMessage('Failed.');
      setTimeout(() => setSaveMessage(null), 3000);
    }
  };

  const saveGamingPc = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/gaming-pc`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(gamingPc) });
      setSaveMessage(res.ok ? 'Gaming PC config saved.' : 'Failed.');
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (e) {
      setSaveMessage('Failed.');
      setTimeout(() => setSaveMessage(null), 3000);
    }
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('video', file);
    try {
      const r = await fetch('/api/video/upload', { method: 'POST', body: fd });
      if (r.ok) {
        const d = await r.json();
        setVideo((v) => ({ ...v, uploadedVideo: d.url }));
        await fetch(`${process.env.REACT_APP_API_URL}/api/video`, { 
          method: 'POST', 
          headers: { 'Content-Type': 'application/json' }, 
          body: JSON.stringify({ ...video, uploadedVideo: d.url }) 
        });
        setSaveMessage('Video uploaded and saved.');
        setTimeout(() => setSaveMessage(null), 3000);
      } else {
        setSaveMessage('Video upload failed.');
        setTimeout(() => setSaveMessage(null), 3000);
      }
    } catch (err) {
      setSaveMessage('Video upload failed.');
      setTimeout(() => setSaveMessage(null), 3000);
    } finally {
      setUploading(false);
    }
  };

  const handleThumbnailUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('thumbnail', file);
    try {
      const r = await fetch('/api/video/thumbnail', { method: 'POST', body: fd });
      if (r.ok) {
        const d = await r.json();
        // Log optimization results if available
        if (d.optimization) {
          console.log(`✅ Thumbnail optimized: ${(d.optimization.savedPercent)}% size reduction`);
        }
        setVideo((v) => ({ ...v, thumbnail: d.url }));
        await fetch(`${process.env.REACT_APP_API_URL}/api/video`, { 
          method: 'POST', 
          headers: { 'Content-Type': 'application/json' }, 
          body: JSON.stringify({ ...video, thumbnail: d.url }) 
        });
        setSaveMessage('Thumbnail uploaded and saved.');
        setTimeout(() => setSaveMessage(null), 3000);
      } else {
        setSaveMessage('Thumbnail upload failed.');
        setTimeout(() => setSaveMessage(null), 3000);
      }
    } catch (err) {
      setSaveMessage('Thumbnail upload failed.');
      setTimeout(() => setSaveMessage(null), 3000);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', pb: 6 }}>
      <Box sx={{ bgcolor: 'primary.main', color: 'white', py: 3 }}>
        <Container maxWidth="lg">
          <Typography variant="h4" fontWeight={700}>Admin Panel</Typography>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>Manage content, images, laptops and gaming PC</Typography>
        </Container>
      </Box>
      <Container maxWidth="lg" sx={{ mt: 3 }}>
        {saveMessage && (
          <Alert severity={saveMessage.includes('saved') ? 'success' : 'error'} onClose={() => setSaveMessage(null)} sx={{ mb: 2 }}>
            {saveMessage}
          </Alert>
        )}
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 2, borderColor: 'divider', mb: 3 }}
        >
          {TAB_OPTIONS.map((opt, i) => (
            <Tab key={opt.value} label={opt.label} sx={{ fontWeight: 600, textTransform: 'none' }} />
          ))}
        </Tabs>

        {/* Content */}
        {TAB_OPTIONS[activeTab]?.value === 'content' && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={700} gutterBottom>Bilingual Content (Arabic | English)</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>Edit text side-by-side.</Typography>
              <Box sx={{ maxHeight: 520, overflow: 'auto' }}>
                {Object.keys(inputValues.ar || {}).map((key) => {
                  const arVal = inputValues.ar[key];
                  const enVal = inputValues.en[key];
                  if (typeof arVal === 'string') {
                    return (
                      <Box key={key} sx={{ mb: 2 }}>
                        <InputLabel sx={{ color: 'text.primary', fontWeight: 600, mb: 0.5 }}>{key}</InputLabel>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <TextField fullWidth size="small" label="Arabic" value={arVal || ''} onChange={(e) => handleContentChange('ar', key, e.target.value)} />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField fullWidth size="small" label="English" value={enVal || ''} onChange={(e) => handleContentChange('en', key, e.target.value)} />
                          </Grid>
                        </Grid>
                      </Box>
                    );
                  }
                  if (typeof arVal === 'object' && !Array.isArray(arVal)) {
                    return (
                      <Box key={key} sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>{key}</Typography>
                        {Object.keys(arVal || {}).map((subKey) => {
                          const subArVal = arVal[subKey];
                          const subEnVal = enVal?.[subKey];
                          
                          // Handle nested arrays
                          if (Array.isArray(subArVal)) {
                            return (
                              <Box key={subKey} sx={{ mb: 2, pl: 2 }}>
                                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1, color: 'text.secondary' }}>{subKey}</Typography>
                                {subArVal.map((item, index) => (
                                  <Box key={index} sx={{ mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                                    <Typography variant="caption" fontWeight={600} sx={{ mb: 1, display: 'block' }}>Item {index + 1}</Typography>
                                    {Object.keys(item || {}).map((field) => (
                                      <Box key={field} sx={{ mb: 1.5 }}>
                                        <InputLabel sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{field}</InputLabel>
                                        <Grid container spacing={2}>
                                          <Grid item xs={12} md={6}>
                                            <TextField fullWidth size="small" multiline={field === 'description'} rows={field === 'description' ? 2 : 1} value={subArVal[index]?.[field] || ''} onChange={(e) => handleNestedArrayItemChange('ar', key, subKey, index, field, e.target.value)} />
                                          </Grid>
                                          <Grid item xs={12} md={6}>
                                            <TextField fullWidth size="small" multiline={field === 'description'} rows={field === 'description' ? 2 : 1} value={subEnVal?.[index]?.[field] || ''} onChange={(e) => handleNestedArrayItemChange('en', key, subKey, index, field, e.target.value)} />
                                          </Grid>
                                        </Grid>
                                      </Box>
                                    ))}
                                  </Box>
                                ))}
                              </Box>
                            );
                          }
                          
                          // Handle simple nested values
                          return (
                            <Box key={subKey} sx={{ mb: 2, pl: 2 }}>
                              <InputLabel sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>{subKey}</InputLabel>
                              <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                  <TextField fullWidth size="small" value={subArVal || ''} onChange={(e) => handleNestedContentChange('ar', key, subKey, e.target.value)} />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                  <TextField fullWidth size="small" value={subEnVal || ''} onChange={(e) => handleNestedContentChange('en', key, subKey, e.target.value)} />
                                </Grid>
                              </Grid>
                            </Box>
                          );
                        })}
                      </Box>
                    );
                  }
                  if (Array.isArray(arVal)) {
                    return (
                      <Box key={key} sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>{key}</Typography>
                        {arVal.map((item, index) => (
                          <Box key={index} sx={{ mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                            {Object.keys(item || {}).map((field) => (
                              <Box key={field} sx={{ mb: 1.5 }}>
                                <InputLabel sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{key}[{index}].{field}</InputLabel>
                                <Grid container spacing={2}>
                                  <Grid item xs={12} md={6}>
                                    <TextField fullWidth size="small" value={arVal[index]?.[field] || ''} onChange={(e) => handleArrayItemChange('ar', key, index, field, e.target.value)} />
                                  </Grid>
                                  <Grid item xs={12} md={6}>
                                    <TextField fullWidth size="small" value={enVal?.[index]?.[field] || ''} onChange={(e) => handleArrayItemChange('en', key, index, field, e.target.value)} />
                                  </Grid>
                                </Grid>
                              </Box>
                            ))}
                          </Box>
                        ))}
                      </Box>
                    );
                  }
                  return null;
                })}
              </Box>
              <Button variant="contained" onClick={saveContent} sx={{ mt: 2 }}>Save Content</Button>
            </CardContent>
          </Card>
        )}

        {/* Images */}
        {TAB_OPTIONS[activeTab]?.value === 'images' && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={700} gutterBottom>Image Management</Typography>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>Home Page Top Slider</Typography>
                <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'info.main', fontWeight: 500 }}>📐 Recommended: 1920x1080 (will be auto-optimized)</Typography>
                {images.hero && <Box component="img" src={images.hero} alt="Hero" sx={{ maxWidth: 300, maxHeight: 150, objectFit: 'cover', borderRadius: 1, mb: 1 }} />}
                <Button variant="outlined" component="label" disabled={uploading}>Upload image<input type="file" accept="image/*" hidden onChange={(e) => handleImageUpload(e, 'hero')} /></Button>
              </Box>
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>Your Logo</Typography>
                <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'info.main', fontWeight: 500 }}>📐 Recommended: 200x200 (square, will be auto-optimized)</Typography>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  {images.brands?.map((img, i) => (
                    <Grid item key={i}>
                      <Box sx={{ position: 'relative', borderRadius: 1, overflow: 'hidden', width: 100, height: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', border: 1, borderColor: 'divider' }}>
                        <Box component="img" src={img} alt={`Brand ${i + 1}`} sx={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                        <Button 
                          size="small" 
                          color="error" 
                          sx={{ position: 'absolute', top: 2, right: 2, bgcolor: 'rgba(255,255,255,0.95)', minWidth: 24, height: 24, padding: 0 }}
                          onClick={async () => {
                            const updatedBrands = images.brands.filter((_, idx) => idx !== i);
                            const updatedImages = { ...images, brands: updatedBrands };
                            setImages(updatedImages);
                            await fetch(`${process.env.REACT_APP_API_URL}/api/images`, {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify(updatedImages)
                            });
                            setSaveMessage('Brand logo removed.');
                            setTimeout(() => setSaveMessage(null), 3000);
                          }}
                        >
                          ✕
                        </Button>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
                <Button variant="outlined" component="label" sx={{ mt: 1 }} disabled={uploading}>Add logo<input type="file" accept="image/*" hidden onChange={(e) => handleImageUpload(e, 'brands')} /></Button>
              </Box>
              <Box>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>Service images</Typography>
                <Typography variant="caption" sx={{ display: 'block', mb: 1.5, color: 'info.main', fontWeight: 500 }}>📐 Recommended: 400x250 (will be auto-optimized)</Typography>
                {localContent.ar?.services?.map((svc, i) => (
                  <Box key={i} sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">Service Image {i+1}</Typography>
                    {images.services?.[`service-${i}`] && <Box component="img" src={images.services[`service-${i}`]} alt="" sx={{ maxWidth: 200, maxHeight: 120, objectFit: 'cover', borderRadius: 1, display: 'block', mt: 0.5 }} />}
                    <Button size="small" variant="outlined" component="label" disabled={uploading}>Upload<input type="file" accept="image/*" hidden onChange={(e) => handleImageUpload(e, `service-${i}`)} /></Button>
                  </Box>
                ))}
              </Box>
              <Divider sx={{ my: 3 }} />
              <Box>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>Gallery - Custom PC Carousel</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Upload images for the custom PC gallery carousel on the home page. Display responsive on mobile and desktop.</Typography>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  {images.gallery?.map((img, i) => (
                    <Grid item xs={6} sm={4} md={3} key={i}>
                      <Box sx={{ position: 'relative', borderRadius: 1, overflow: 'hidden' }}>
                        <Box component="img" src={img} alt="" sx={{ width: '100%', height: 120, objectFit: 'cover', border: 1, borderRadius: 1 }} />
                        <Button 
                          size="small" 
                          color="error" 
                          sx={{ position: 'absolute', top: 4, right: 4, bgcolor: 'rgba(255,255,255,0.9)' }}
                          onClick={async () => {
                            const u = images.gallery.filter((_, idx) => idx !== i);
                            setImages({ ...images, gallery: u });
                            await fetch(`${process.env.REACT_APP_API_URL}/api/images`, {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({ ...images, gallery: u })
                            });
                            setSaveMessage('Gallery image removed.');
                            setTimeout(() => setSaveMessage(null), 3000);
                          }}
                        >
                          ✕
                        </Button>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
                <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'info.main', fontWeight: 500 }}>📐 Recommended: 800x600 (4:3 ratio, will be auto-optimized)</Typography>
                <Button variant="outlined" component="label" disabled={uploading}>Add Gallery Image<input type="file" accept="image/*" hidden onChange={(e) => handleImageUpload(e, 'gallery')} /></Button>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Video */}
        {TAB_OPTIONS[activeTab]?.value === 'video' && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={700} gutterBottom>Video</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 520 }}>
                <TextField select SelectProps={{ native: true }} label="Type" value={video.type} onChange={(e) => setVideo((v) => ({ ...v, type: e.target.value }))} fullWidth size="small">
                  <option value="youtube">YouTube</option>
                  <option value="upload">Upload</option>
                </TextField>
                {video.type === 'youtube' ? (
                  <TextField label="YouTube URL" value={video.youtubeUrl || ''} onChange={(e) => setVideo((v) => ({ ...v, youtubeUrl: e.target.value }))} fullWidth size="small" placeholder="https://youtube.com/watch?v=..." />
                ) : (
                  <>
                    {video.uploadedVideo && <Box component="video" src={video.uploadedVideo} controls sx={{ maxWidth: 400, maxHeight: 200, borderRadius: 1 }} />}
                    <Button variant="outlined" component="label" disabled={uploading}>Upload video (max 25MB)<input type="file" accept="video/*" hidden onChange={handleVideoUpload} /></Button>
                    {video.thumbnail && <Box component="img" src={video.thumbnail} alt="Thumb" sx={{ maxWidth: 200, maxHeight: 120, objectFit: 'cover', borderRadius: 1, display: 'block', mt: 1 }} />}
                    <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'info.main', fontWeight: 500 }}>📐 Thumbnail recommended: 320x180 (16:9 ratio, will be auto-optimized)</Typography>
                    <Button variant="outlined" size="small" component="label" disabled={uploading}>Upload thumbnail<input type="file" accept="image/*" hidden onChange={handleThumbnailUpload} /></Button>
                  </>
                )}
              </Box>
              <Button variant="contained" onClick={saveVideo} sx={{ mt: 2 }}>Save Video</Button>
            </CardContent>
          </Card>
        )}

        {/* Laptops */}
        {TAB_OPTIONS[activeTab]?.value === 'laptops' && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={700} gutterBottom>Premium Laptops</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Add or edit laptops. Fields: brand, name, condition (new/refurbished/used), price, originalPrice, cpu, ram, storage, display, image.</Typography>
              {laptops.map((lap, idx) => (
                <Card key={lap.id || idx} variant="outlined" sx={{ p: 2, mb: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={2}>
                      {lap.image && <Box component="img" src={lap.image} alt="" sx={{ width: '100%', maxHeight: 100, objectFit: 'cover', borderRadius: 1 }} />}
                      <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'info.main', fontWeight: 500 }}>📐 Recommended: 500x500 or 600x600 (will be auto-optimized)</Typography>
                    </Grid>
                    <Grid item xs={12} md={10}>
                      <Grid container spacing={2}>
                        {['brand', 'name', 'condition', 'price', 'originalPrice', 'cpu', 'ram', 'storage', 'display'].map((f) => (
                          <Grid item xs={12} sm={6} key={f}>
                            <TextField label={f} size="small" fullWidth value={lap[f] || ''} onChange={(e) => { const u = [...laptops]; u[idx] = { ...u[idx], [f]: e.target.value }; setLaptops(u); }} />
                          </Grid>
                        ))}
                      </Grid>
                      <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                        <Button size="small" variant="outlined" component="label" disabled={uploading}>Change image<input type="file" accept="image/*" hidden onChange={async (e) => { const file = e.target.files[0]; if (!file) return; const fd = new FormData(); fd.append('image', file); const res = await fetch('/api/images/upload', { method: 'POST', body: fd }); if (res.ok) { const d = await res.json(); const u = [...laptops]; u[idx] = { ...u[idx], image: d.url }; setLaptops(u); await fetch(`${process.env.REACT_APP_API_URL}/api/laptops`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(u) });  } } } /></Button>
                        <Button size="small" color="error" onClick={async () => { 
                          const laptopId = laptops[idx]?.id;
                          const u = laptops.filter((_, i) => i !== idx); 
                          setLaptops(u); 
                          // Delete from database immediately if it has an ID
                          if (laptopId) {
                            try {
                              await fetch(`${process.env.REACT_APP_API_URL}/api/laptops/${laptopId}`, { method: 'DELETE' });
                            } catch (err) {
                              console.error('Failed to delete laptop:', err);
                            }
                          }
                        }}>Remove</Button>
                      </Box>
                    </Grid>
                  </Grid>
                </Card>
              ))}
              <Button variant="outlined" onClick={async () => { const u = [...laptops, { id: null, brand: '', name: '', condition: 'new', price: '', originalPrice: '', cpu: '', ram: '', storage: '', display: '', image: '' }]; setLaptops(u); }}>Add Laptop</Button>
              <Button variant="contained" onClick={saveLaptops} sx={{ ml: 2 }}>Save All Laptops</Button>
            </CardContent>
          </Card>
        )}

        {/* Gaming PC */}
        {TAB_OPTIONS[activeTab]?.value === 'gamingPc' && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={700} gutterBottom>Gaming PC Build</Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField type="number" label="Base cost ($)" fullWidth size="small" value={gamingPc.baseCost ?? 299} onChange={(e) => setGamingPc((p) => ({ ...p, baseCost: Number(e.target.value) || 0 }))} />
                </Grid>
                {['baseCostLabelAr', 'baseCostLabelEn', 'orderButtonAr', 'orderButtonEn', 'disclaimerAr', 'disclaimerEn'].map((k) => (
                  <Grid item xs={12} key={k}><TextField label={k} fullWidth size="small" value={gamingPc[k] || ''} onChange={(e) => setGamingPc((p) => ({ ...p, [k]: e.target.value }))} /></Grid>
                ))}
              </Grid>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>Component options (name, performance, price)</Typography>
              {['cpu', 'gpu', 'ram', 'storage', 'cooling', 'psu'].map((cat) => (
                <Box key={cat} sx={{ mb: 3 }}>
                  <Typography variant="body2" color="primary" fontWeight={600} sx={{ textTransform: 'uppercase', mb: 1 }}>{cat}</Typography>
                  {(gamingPc.options?.[cat] || []).map((opt, oIdx) => (
                    <Box key={oIdx} sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 1 }}>
                      <TextField size="small" placeholder="Name" value={opt.name || ''} onChange={(e) => { const o = [...(gamingPc.options[cat] || [])]; o[oIdx] = { ...o[oIdx], name: e.target.value }; setGamingPc((p) => ({ ...p, options: { ...p.options, [cat]: o } })); }} sx={{ width: 220 }} />
                      <TextField size="small" placeholder="Performance" value={opt.performance || ''} onChange={(e) => { const o = [...(gamingPc.options[cat] || [])]; o[oIdx] = { ...o[oIdx], performance: e.target.value }; setGamingPc((p) => ({ ...p, options: { ...p.options, [cat]: o } })); }} sx={{ width: 140 }} />
                      <TextField type="number" size="small" placeholder="Price" value={opt.price ?? ''} onChange={(e) => { const o = [...(gamingPc.options[cat] || [])]; o[oIdx] = { ...o[oIdx], price: Number(e.target.value) || 0 }; setGamingPc((p) => ({ ...p, options: { ...p.options, [cat]: o } })); }} sx={{ width: 100 }} />
                      <Button size="small" color="error" onClick={async () => { 
                        const o = (gamingPc.options[cat] || []).filter((_, i) => i !== oIdx);
                        setGamingPc((p) => ({ ...p, options: { ...p.options, [cat]: o } }));
                        // Delete from database immediately
                        try {
                          await fetch(`${process.env.REACT_APP_API_URL}/api/gaming-pc/options/${cat}/${encodeURIComponent(opt.name)}`, { method: 'DELETE' });
                        } catch (err) {
                          console.error('Failed to delete option:', err);
                        }
                      }}>Remove</Button>
                    </Box>
                  ))}
                  <Button size="small" variant="outlined" onClick={() => { const o = [...(gamingPc.options[cat] || []), { name: '', performance: 'good', price: 0 }]; setGamingPc((p) => ({ ...p, options: { ...p.options, [cat]: o } })); }}>Add {cat} option</Button>
                </Box>
              ))}
              <Button variant="contained" onClick={saveGamingPc}>Save Gaming PC Config</Button>
            </CardContent>
          </Card>
        )}
      </Container>
    </Box>
  );
};

export default Admin;
